'use client';
/**
 * Project 项目业务逻辑
 */
import { useTranslations } from 'next-intl';
import { toast } from '@/app/components/ui/use-toast';
import { useGlobalModal, useUserInfo } from '@/app/hooks';
import { useStore } from '@/app/providers/store';
import { reportEvent } from '@/app/utils';
import { useCreateCanvas, useCreateChat, useCreateProject, useGetProjectList } from '../apis';

// 项目初始化
export function useCreateNewProject() {
  const t = useTranslations();
  const { createProjectAsync } = useCreateProject();
  const { createCanvasAsync } = useCreateCanvas();
  const { createChatAsync } = useCreateChat();
  const { fetchAgentInviteInfo } = useUserInfo();

  const { setAgentProjectDetail, setAgentCanvasDetail, agentCanvasDetail, agentProjectDetail } = useStore((state) => ({
    setAgentProjectDetail: state.setAgentProjectDetail,
    setAgentCanvasDetail: state.setAgentCanvasDetail,
    agentCanvasDetail: state.agentCanvasDetail,
    agentProjectDetail: state.agentProjectDetail,
  }));

  const { fetchProjectListAsync } = useGetProjectList();
  const { open: openPlanDetailDialog } = useGlobalModal('planDetailDialog');
  const { open: openPurchaseCreditsDialog } = useGlobalModal('purchaseCreditsDialog');
  const { open: openConfirmDialog } = useGlobalModal('confirmDialog');

  const { userInfo, creditsInfo, agentInviteInfo } = useStore((state) => ({
    userInfo: state.userInfo,
    creditsInfo: state.creditsInfo,
    agentInviteInfo: state.agentInviteInfo,
  }));
  const isTeamSpace = useStore((state) => state.isTeamSpace);

  const createNewChat = async (): Promise<{ projectId: string; canvasId: string; chatId: string } | undefined> => {
    let projectId = agentProjectDetail?.id;
    if (!projectId) {
      const { data, error } = await createProjectAsync({
        name: agentProjectDetail?.name || `${Date.now()}`,
      });
      if (error) {
        if (['ReadOnlyMode', 'ProjectOverLimit'].includes(error.reason)) {
          openPlanDetailDialog({ fromPage: 'project_create' });
          reportEvent('vip_price_modal_exposure', {
            from_page: 'project_create',
          });
          return;
        }
        if (error.reason === 'CreditInsufficient') {
          if (isTeamSpace) {
            openConfirmDialog({
              title: t('popup.dialog.error.teamCreditsInsufficientContactAdmin.title'),
              description: t('popup.dialog.error.teamCreditsInsufficientContactAdmin.content'),
              primaryButtonText: t('popup.dialog.error.confirm'),
            });
          } else {
            openPurchaseCreditsDialog();
          }
          reportEvent('credit_modal_exposure', {
            from_page: 'project_create',
          });
          return;
        }
      }
      // 创建成果，更新邀请信息
      fetchAgentInviteInfo();
      if (data?.id) {
        projectId = data?.id;
        setAgentProjectDetail(data);
      } else {
        toast({ type: 'error', message: t('agentProject.error.createProjectFailed') });
        return;
      }
    }
    let canvasId = agentCanvasDetail?.id;
    if (!canvasId) {
      const { data: canvasData } = await createCanvasAsync({ project_id: projectId });
      if (canvasData?.id) {
        setAgentCanvasDetail(canvasData);
        canvasId = canvasData?.id;
      } else {
        toast({ type: 'error', message: t('agentProject.error.createCanvasFailed') });
        return;
      }
    }
    const { data: chatData } = await createChatAsync(canvasId);
    if (chatData?.id) {
      return {
        projectId,
        canvasId,
        chatId: chatData.id,
      };
    }
    toast({ type: 'error', message: t('agentProject.error.createChatFailed') });
    return;
  };

  // 判断是否可以创建项目
  const canCreateProject = async () => {
    const subsPlan = userInfo?.subs_plan;
    // 免费版、标准版、专业版
    if (subsPlan === 'free' || subsPlan === 'standard_v2' || subsPlan === 'premium_v2') {
      const { data } = await fetchProjectListAsync(1);
      // project_quota 为 0 表示不能创建
      if ((data?.project_quota || 0) <= 0) {
        // 是否可邀请好友创建免费项目，提示邀请好友弹窗，否则提示升级套餐
        if (agentInviteInfo?.status === 'invited') {
          return 'invited';
        }
        return 'upgrade';
      }
    }
    // 积分不足
    if ((creditsInfo?.total || 0) <= 400) {
      if ((agentInviteInfo?.has_free_project_count || 0) <= 0) {
        // 免费版提示升级套餐，其他版本提示购买积分
        if (subsPlan === 'free') {
          return 'upgrade';
        }
        return 'purchase';
      }
    }
    // 可以创建
    return 'create';
  };

  return { createNewChat, canCreateProject };
}
